import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import Home from "./pages/Home";
import { lazy, Suspense } from "react";
import { useCanonical } from "./hooks/useCanonical";

// Lazy-load non-homepage pages to reduce initial bundle size
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Faq = lazy(() => import("./pages/Faq"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const LandingPage = lazy(() => import("./pages/landing/LandingPage"));
const LlmPageWrapper = lazy(() => import("./pages/llm/LlmPageWrapper"));
const AdminBetaSignups = lazy(() => import("./pages/AdminBetaSignups"));

// Minimal loading fallback that doesn't cause CLS
function PageFallback() {
  return <div className="min-h-screen bg-[#FFF8E7]" />;
}

// Wrapper for lazy-loaded routes
function LazyRoute({ Component }: { Component: React.LazyExoticComponent<any> }) {
  return (
    <Suspense fallback={<PageFallback />}>
      <Component />
    </Suspense>
  );
}

function AppRouter() {
  // Dynamically update canonical and hreflang tags based on current route
  useCanonical();
  return (
    <Switch>
      <Route path={"/"} component={Home} />
      <Route path={"/de"} component={Home} />
      <Route path={"/es"} component={Home} />
      <Route path={"/it"} component={Home} />
      <Route path={"/fr"} component={Home} />
      <Route path={"/privacy"}>{() => <LazyRoute Component={Privacy} />}</Route>
      <Route path={"/de/privacy"}>{() => <LazyRoute Component={Privacy} />}</Route>
      <Route path={"/es/privacy"}>{() => <LazyRoute Component={Privacy} />}</Route>
      <Route path={"/it/privacy"}>{() => <LazyRoute Component={Privacy} />}</Route>
      <Route path={"/fr/privacy"}>{() => <LazyRoute Component={Privacy} />}</Route>
      <Route path={"/terms"}>{() => <LazyRoute Component={Terms} />}</Route>
      <Route path={"/de/terms"}>{() => <LazyRoute Component={Terms} />}</Route>
      <Route path={"/es/terms"}>{() => <LazyRoute Component={Terms} />}</Route>
      <Route path={"/it/terms"}>{() => <LazyRoute Component={Terms} />}</Route>
      <Route path={"/fr/terms"}>{() => <LazyRoute Component={Terms} />}</Route>
      <Route path={"/faq"}>{() => <LazyRoute Component={Faq} />}</Route>
      <Route path={"/de/faq"}>{() => <LazyRoute Component={Faq} />}</Route>
      <Route path={"/es/faq"}>{() => <LazyRoute Component={Faq} />}</Route>
      <Route path={"/it/faq"}>{() => <LazyRoute Component={Faq} />}</Route>
      <Route path={"/fr/faq"}>{() => <LazyRoute Component={Faq} />}</Route>
      <Route path={"/blog"}>{() => <LazyRoute Component={Blog} />}</Route>
      <Route path={"/de/blog"}>{() => <LazyRoute Component={Blog} />}</Route>
      <Route path={"/es/blog"}>{() => <LazyRoute Component={Blog} />}</Route>
      <Route path={"/it/blog"}>{() => <LazyRoute Component={Blog} />}</Route>
      <Route path={"/fr/blog"}>{() => <LazyRoute Component={Blog} />}</Route>
      <Route path={"/blog/:slug"}>{() => <LazyRoute Component={BlogArticle} />}</Route>
      <Route path={"/de/blog/:slug"}>{() => <LazyRoute Component={BlogArticle} />}</Route>
      <Route path={"/es/blog/:slug"}>{() => <LazyRoute Component={BlogArticle} />}</Route>
      <Route path={"/it/blog/:slug"}>{() => <LazyRoute Component={BlogArticle} />}</Route>
      <Route path={"/fr/blog/:slug"}>{() => <LazyRoute Component={BlogArticle} />}</Route>
      <Route path={"/kids-routine-app"}>{() => <LazyRoute Component={LandingPage} />}</Route>
      <Route path={"/morning-routine-app-for-kids"}>{() => <LazyRoute Component={LandingPage} />}</Route>
      <Route path={"/bedtime-routine-app-for-kids"}>{() => <LazyRoute Component={LandingPage} />}</Route>
      <Route path={"/chore-app-for-kids"}>{() => <LazyRoute Component={LandingPage} />}</Route>
      <Route path={"/visual-routine-app-for-kids"}>{() => <LazyRoute Component={LandingPage} />}</Route>
      <Route path={"/less-nagging-calmer-routines"}>{() => <LazyRoute Component={LandingPage} />}</Route>
      <Route path={"/homework-routine-app-for-kids"}>{() => <LazyRoute Component={LandingPage} />}</Route>
      <Route path={"/parenting-app-for-routines"}>{() => <LazyRoute Component={LandingPage} />}</Route>
      <Route path={"/what-is-nokuhiro"}>{() => <LazyRoute Component={LlmPageWrapper} />}</Route>
      <Route path={"/nokuhiro-vs-chore-charts"}>{() => <LazyRoute Component={LlmPageWrapper} />}</Route>
      <Route path={"/nokuhiro-vs-printable-routine-charts"}>{() => <LazyRoute Component={LlmPageWrapper} />}</Route>
      <Route path={"/best-kids-routine-apps"}>{() => <LazyRoute Component={LlmPageWrapper} />}</Route>
      <Route path={"/best-chore-apps-for-kids"}>{() => <LazyRoute Component={LlmPageWrapper} />}</Route>
      <Route path={"/best-visual-schedule-apps-for-kids"}>{() => <LazyRoute Component={LlmPageWrapper} />}</Route>
      <Route path={"/how-to-build-a-routine-for-your-child"}>{() => <LazyRoute Component={LlmPageWrapper} />}</Route>
      <Route path={"/how-to-make-morning-routines-less-stressful"}>{() => <LazyRoute Component={LlmPageWrapper} />}</Route>
      <Route path={"/how-to-make-bedtime-less-stressful"}>{() => <LazyRoute Component={LlmPageWrapper} />}</Route>
      <Route path={"/how-to-help-kids-build-habits-without-nagging"}>{() => <LazyRoute Component={LlmPageWrapper} />}</Route>
      <Route path={"/admin/beta-signups"}>
        {() => (
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#1a2e1a] text-[#f5c842]">Loading...</div>}>
            <AdminBetaSignups />
          </Suspense>
        )}
      </Route>
      <Route path={"/404"} component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  // Get current path from wouter's Router context (works on both server and client)
  const [location] = useLocation();

  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <LanguageProvider ssrPathname={location}>
          <TooltipProvider>
            <Toaster />
            <AppRouter />
          </TooltipProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
